import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, Lock, CheckCircle, Tag, Clock, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Challenges = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState({});

  useEffect(() => {
    fetchChallenges();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Auth error:', error);
      }
    }
  };

  const fetchChallenges = async () => {
    try {
      // Obtener challenges de la colección principal de challenges
      const response = await axios.get(`${BACKEND_URL}/api/challenges`);
      setChallenges(response.data || []);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  // Verificar si el usuario tiene acceso gratuito a UN challenge específico
  const hasFreeChallengeAccess = (challenge) => {
    if (!challenge) return false;
    
    const accessType = challenge.access_type;
    const accessLevels = challenge.access_levels || [];
    
    // También verificar sistema legacy (level/levels)
    const legacyLevel = challenge.level;
    const legacyLevels = challenge.levels || [];
    
    // Si es gratuito para todos
    if (accessType === 'free') return true;
    
    // Si no hay usuario logueado
    if (!user) return false;
    
    // Mapeo de niveles
    const levelMap = {
      'principiante': 'nivel_1',
      'intermedio': 'nivel_2',
      'premium': 'premium',
      'avanzado': 'premium',
      'nivel_1': 'nivel_1',
      'nivel_2': 'nivel_2'
    };
    const userLevel = levelMap[user.subscription_level] || user.subscription_level;
    
    // Si el acceso es por niveles y el usuario está en uno de esos niveles
    if (accessType === 'levels' && accessLevels.length > 0) {
      if (accessLevels.includes(userLevel) || accessLevels.includes(user.subscription_level)) {
        return true;
      }
    }
    
    // Sistema legacy: verificar level/levels del challenge
    if (legacyLevel === 'premium' || legacyLevels.includes('premium')) {
      if (user.subscription_level === 'premium' || user.subscription_level === 'avanzado') {
        return true;
      }
    }
    
    return false;
  };

  // Aplicar código de descuento usando la API centralizada
  const applyDiscountCode = async (challengeId, code) => {
    if (!code || !code.trim()) {
      toast.error('Ingresa un código de descuento');
      return;
    }

    const challenge = challenges.find(c => c.challenge_id === challengeId);
    if (!challenge) return;

    try {
      const response = await axios.post(`${BACKEND_URL}/api/validate-discount`, {
        code: code.trim(),
        product_id: challengeId,
        original_price: challenge.price
      });

      if (response.data.valid) {
        setAppliedDiscount({
          ...appliedDiscount,
          [challengeId]: {
            code: code.trim(),
            percentage: response.data.discount_type === 'percentage' ? response.data.discount_value : null,
            fixed: response.data.discount_type === 'fixed' ? response.data.discount_value : null,
            final_price: response.data.final_price
          }
        });
        toast.success(`¡Código aplicado! Nuevo precio: $${response.data.final_price}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de descuento inválido');
    }
  };

  // Calcular precio final
  const getFinalPrice = (challenge) => {
    const discount = appliedDiscount[challenge.challenge_id];
    if (discount) {
      if (discount.final_price !== undefined) {
        return discount.final_price;
      }
      if (discount.percentage) {
        return (challenge.price * (1 - discount.percentage / 100)).toFixed(2);
      }
      if (discount.fixed) {
        return Math.max(0, challenge.price - discount.fixed).toFixed(2);
      }
    }
    return challenge.price;
  };

  const handleEnroll = async (challenge) => {
    const token = localStorage.getItem('session_token');
    const challengeId = challenge.challenge_id;
    
    // Obtener el descuento aplicado para este challenge (si existe)
    const discountApplied = appliedDiscount[challengeId];
    const discountParam = discountApplied?.code ? `&discount=${discountApplied.code}` : '';
    
    // Si el usuario tiene acceso gratuito a este challenge específico (requiere login)
    if (token && hasFreeChallengeAccess(challenge)) {
      try {
        await axios.post(`${BACKEND_URL}/api/challenges/${challengeId}/enroll-free`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('¡Te has inscrito al challenge!');
        // Redirigir a la página del challenge
        navigate(`/challenge/${challengeId}`);
        return;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al inscribirse');
        return;
      }
    }

    // Redirigir a la página de checkout para challenges de pago
    // El checkout permite pagar sin estar logueado (guest checkout)
    navigate(`/checkout?challenge=${challengeId}${discountParam}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Separar challenges activos y próximamente
  const activeChallenges = challenges.filter(c => c.status !== 'coming_soon');
  const comingSoonChallenges = challenges.filter(c => c.status === 'coming_soon');

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white" style={{fontFamily: 'Syne, sans-serif'}} data-testid="challenges-title">
            Challenges
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Retáte a ti misma con nuestros challenges de tiempo limitado
          </p>
          {user && ['premium', 'avanzado'].includes(user.subscription_level) && (
            <div className="mt-4 inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full border border-green-500/30">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Tienes acceso gratuito a challenges seleccionados</span>
            </div>
          )}
        </div>

        {challenges.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-zinc-400">No hay challenges disponibles en este momento</p>
            <p className="text-zinc-500 mt-2">Vuelve pronto para ver nuevos retos</p>
          </div>
        ) : (
          <>
            {/* Challenges Activos */}
            {activeChallenges.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  🔥 Challenges Disponibles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {activeChallenges.map((challenge) => (
                    <ChallengeCard
                      key={challenge.challenge_id}
                      challenge={challenge}
                      isFreeForUser={hasFreeChallengeAccess(challenge)}
                      discount={appliedDiscount[challenge.challenge_id]}
                      finalPrice={getFinalPrice(challenge)}
                      discountCode={discountCode}
                      setDiscountCode={setDiscountCode}
                      applyDiscountCode={applyDiscountCode}
                      handleEnroll={handleEnroll}
                      user={user}
                      isComingSoon={false}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Challenges Próximamente */}
            {comingSoonChallenges.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-yellow-400" />
                  Próximamente
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {comingSoonChallenges.map((challenge) => (
                    <ChallengeCard
                      key={challenge.challenge_id}
                      challenge={challenge}
                      isFreeForUser={false}
                      discount={null}
                      finalPrice={challenge.price}
                      discountCode=""
                      setDiscountCode={() => {}}
                      applyDiscountCode={() => {}}
                      handleEnroll={() => {}}
                      user={user}
                      isComingSoon={true}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Si solo hay coming soon */}
            {activeChallenges.length === 0 && comingSoonChallenges.length > 0 && (
              <div className="text-center py-8 mt-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl">
                <p className="text-xl text-zinc-400">No hay challenges activos en este momento</p>
                <p className="text-zinc-500 mt-2">¡Pero tenemos nuevos retos en camino! 🚀</p>
              </div>
            )}
          </>
        )}

        {/* Información adicional */}
        <div className="mt-16 bg-zinc-900 border border-zinc-800 rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">¿Cómo funcionan los Challenges?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left max-w-4xl mx-auto">
            <div className="p-4">
              <div className="w-10 h-10 bg-pink-900/50 rounded-full flex items-center justify-center mb-3 border border-pink-700">
                <span className="text-pink-400 font-bold">1</span>
              </div>
              <h3 className="font-bold mb-2 text-white">Inscríbete</h3>
              <p className="text-sm text-zinc-400">Paga una única vez para acceder al challenge completo</p>
            </div>
            <div className="p-4">
              <div className="w-10 h-10 bg-pink-900/50 rounded-full flex items-center justify-center mb-3 border border-pink-700">
                <span className="text-pink-400 font-bold">2</span>
              </div>
              <h3 className="font-bold mb-2 text-white">Entrena</h3>
              <p className="text-sm text-zinc-400">Sigue la rutina diaria durante el tiempo del reto</p>
            </div>
            <div className="p-4">
              <div className="w-10 h-10 bg-pink-900/50 rounded-full flex items-center justify-center mb-3 border border-pink-700">
                <span className="text-pink-400 font-bold">3</span>
              </div>
              <h3 className="font-bold mb-2 text-white">Transforma</h3>
              <p className="text-sm text-zinc-400">Comparte tu progreso y celebra tus resultados</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de tarjeta de Challenge
const ChallengeCard = ({ 
  challenge, 
  isFreeForUser, 
  discount, 
  finalPrice, 
  discountCode, 
  setDiscountCode, 
  applyDiscountCode, 
  handleEnroll, 
  user,
  isComingSoon 
}) => {
  return (
    <div 
      className={`bg-zinc-900 border rounded-3xl overflow-hidden transition-all ${
        isComingSoon 
          ? 'border-yellow-500/30 opacity-80' 
          : 'border-zinc-800 hover:border-pink-500/50'
      }`}
      data-testid={`challenge-${challenge.challenge_id}`}
    >
      {challenge.image_url && (
        <div className="relative">
          <img 
            src={challenge.image_url.startsWith('http') ? challenge.image_url : `${BACKEND_URL}${challenge.image_url}`} 
            alt={challenge.title} 
            className={`w-full h-48 object-cover ${isComingSoon ? 'filter grayscale' : ''}`}
          />
          {isComingSoon && (
            <div className="absolute top-4 left-4 bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Próximamente
            </div>
          )}
          {isFreeForUser && !isComingSoon && (
            <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
              GRATIS
            </div>
          )}
          {challenge.discount_code && !discount && !isComingSoon && (
            <div className="absolute top-4 left-4 bg-pink-600 text-white px-3 py-1 rounded-full text-sm font-bold">
              {challenge.discount_percentage}% OFF disponible
            </div>
          )}
        </div>
      )}
      
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2 text-white">{challenge.title}</h3>
        {/* whitespace-pre-line keeps the line breaks the admin typed; the clamp is
            relaxed from 2 to 6 so the list stays tidy while still showing the
            structure of a multi-line description (detail page shows it in full). */}
        <p className="text-zinc-400 text-sm mb-4 whitespace-pre-line line-clamp-6">{challenge.description}</p>
        
        {(challenge.start_date || challenge.end_date) && (
          <div className="space-y-2 text-sm mb-4">
            <div className="flex items-center text-zinc-400">
              <Calendar className="w-4 h-4 mr-2" />
              {challenge.start_date && new Date(challenge.start_date).toLocaleDateString('es-ES')} 
              {challenge.start_date && challenge.end_date && ' - '}
              {challenge.end_date && new Date(challenge.end_date).toLocaleDateString('es-ES')}
            </div>
          </div>
        )}

        {/* Precio */}
        {!isComingSoon && (
          <div className="mb-4">
            {isFreeForUser ? (
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-green-400">GRATIS</span>
                <span className="text-zinc-500 line-through">${challenge.price}</span>
              </div>
            ) : challenge.original_price && challenge.original_price > challenge.price ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xl text-zinc-500 line-through">${challenge.original_price}</span>
                <span className="text-2xl font-bold text-green-400">${finalPrice}</span>
                <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full">
                  -{Math.round(((challenge.original_price - challenge.price) / challenge.original_price) * 100)}%
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-white">${finalPrice}</span>
                {discount && (
                  <span className="text-zinc-500 line-through">${challenge.price}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Código de descuento */}
        {!isComingSoon && !isFreeForUser && !discount && (
          <div className="mb-4 p-4 bg-pink-500/10 rounded-xl border border-pink-500/30">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-pink-500" />
              <span className="text-sm font-bold text-pink-400">¿Tienes un código de descuento?</span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Aplícalo aquí antes de proceder al pago</p>
            <div className="flex gap-2">
              <Input
                placeholder="Ej: BIENVENIDA10"
                className="text-sm bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                data-testid="discount-code-input"
              />
              <Button 
                size="sm"
                className="bg-pink-600 hover:bg-pink-500 text-white"
                onClick={() => applyDiscountCode(challenge.challenge_id, discountCode)}
                data-testid="apply-discount-btn"
              >
                Aplicar
              </Button>
            </div>
          </div>
        )}

        {!isComingSoon && discount && (
          <div className="mb-4 p-4 bg-green-500/10 rounded-xl border border-green-500/30">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-bold text-green-400">¡Código aplicado!</span>
            </div>
            <p className="text-sm text-green-400 ml-7">
              {discount.code}: {discount.percentage ? `${discount.percentage}% de descuento` : `$${discount.fixed} de descuento`}
            </p>
          </div>
        )}

        {/* Botones de acción */}
        <div className="space-y-2">
          {/* Botón Ver Detalles - siempre visible */}
          <Link to={`/challenge/${challenge.challenge_id}`}>
            <Button 
              variant="outline"
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full py-4"
            >
              <Eye className="w-4 h-4 mr-2" />
              Ver Detalles
            </Button>
          </Link>
          
          {/* Botón de inscripción */}
          {isComingSoon ? (
            <Button 
              disabled
              className="w-full bg-yellow-600/50 text-yellow-200 rounded-full py-4 font-bold cursor-not-allowed"
            >
              <Clock className="w-4 h-4 mr-2" />
              Próximamente
            </Button>
          ) : isFreeForUser ? (
            <Button 
              onClick={() => handleEnroll(challenge)}
              className="w-full bg-green-600 hover:bg-green-500 text-white rounded-full py-4 font-bold"
            >
              Inscribirse Gratis
            </Button>
          ) : (
            <Button 
              onClick={() => handleEnroll(challenge)}
              className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-4 font-bold"
            >
              Pagar ${finalPrice}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Challenges;
